import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ActivityRelatedTo, ActivityResponse } from '@orion/contracts';
import { Avatar, Button, Card, EmptyState, Menu } from '@orion/ui';
import { PageHeader } from '../../app/shell.js';
import { disambiguateNames } from '../../lib/disambiguate-names.js';
import { useAssignableMembersQuery } from '../me/api.js';
import { useLeadsQuery } from '../leads/api.js';
import { useContactsQuery } from '../contacts/api.js';
import { useCompaniesQuery } from '../companies/api.js';
import { useDealsQuery } from '../deals/api.js';
import { LogActivityComposer } from './LogActivityComposer.js';
import { useActivityFeedQuery, useActivityWeeklySummaryQuery, useStaleLeadsQuery } from './api.js';

const KIND_LABELS: Record<string, string> = {
  created: 'Created',
  assigned: 'Assigned',
  stage_changed: 'Stage changed',
  converted: 'Converted',
  note: 'Note',
  call: 'Call',
  email: 'Email',
  meeting: 'Meeting',
  task_completed: 'Task',
  won: 'Won',
  lost: 'Lost',
  system: 'System',
};

const RICH_KINDS = new Set(['call', 'email', 'meeting', 'note']);

const KIND_STYLE: Record<string, { bg: string; fg: string; path: string; isRect?: boolean }> = {
  call: { bg: '#E3F5F3', fg: '#0F6B63', path: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2' },
  email: { bg: '#ECEFFD', fg: '#2439B3', path: 'm3 7 9 6 9-6', isRect: true },
  meeting: { bg: '#FEF3E2', fg: '#8A4B06', path: 'M16 3v4M8 3v4M3 10h18', isRect: true },
  note: { bg: '#F1ECFD', fg: '#5B34B0', path: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9zM14 3v6h6' },
  assigned: { bg: '#EFEEEA', fg: '#454A54', path: 'M12 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4.4 3.6-8 8-8s8 3.6 8 8' },
  stage_changed: { bg: '#F1ECFD', fg: '#5B34B0', path: 'M5 12h14M13 6l6 6-6 6' },
  task_completed: { bg: '#E6F4EC', fg: '#1E7A4C', path: 'm8 12 3 3 5-6', isRect: true },
  won: { bg: '#E6F4EC', fg: '#1E7A4C', path: 'm12 3 2.8 5.7 6.2.9-4.5 4.4 1 6.2L12 17.3 6.5 20.2l1-6.2L3 9.6l6.2-.9z' },
  lost: { bg: '#FBE9E7', fg: '#B42318', path: 'M18 6 6 18M6 6l12 12' },
  created: { bg: '#ECEFFD', fg: '#2439B3', path: 'M12 5v14M5 12h14' },
  system: { bg: '#EFEEEA', fg: '#454A54', path: 'M12 8v4l3 2' },
};

const KIND_FILTERS = [
  { value: undefined, label: 'All' },
  { value: 'call', label: 'Calls' },
  { value: 'email', label: 'Emails' },
  { value: 'meeting', label: 'Meetings' },
  { value: 'note', label: 'Notes' },
  { value: 'task_completed', label: 'Tasks' },
  { value: 'stage_changed', label: 'Status changes' },
  { value: 'assigned', label: 'Assignments' },
] as const;

const SUMMARY_KINDS = [
  { kind: 'call', label: 'Calls', color: '#0F6B63' },
  { kind: 'email', label: 'Emails', color: '#2E47D6' },
  { kind: 'meeting', label: 'Meetings', color: '#F2B25C' },
  { kind: 'note', label: 'Notes', color: '#5B34B0' },
] as const;

function startOfWeek(offset: number): Date {
  const now = new Date();
  const day = now.getDay();
  const mondayDiff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() + mondayDiff + offset * 7);
  return monday;
}

function dayLabel(iso: string): { label: string; sub: string } {
  const date = new Date(iso);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const sub = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  return { label: isToday ? 'Today' : date.toLocaleDateString('en-US', { weekday: 'long' }), sub };
}

function RelatedLink({ relatedTo, children }: { relatedTo: ActivityRelatedTo; children: ReactNode }) {
  const routes = {
    lead: { to: '/leads/$leadId' as const, params: { leadId: relatedTo.id } },
    contact: { to: '/contacts/$contactId' as const, params: { contactId: relatedTo.id } },
    company: { to: '/companies/$companyId' as const, params: { companyId: relatedTo.id } },
    opportunity: { to: '/deals/$dealId' as const, params: { dealId: relatedTo.id } },
  };
  const route = routes[relatedTo.type];
  return <Link {...route}>{children}</Link>;
}

const RELATED_PREFIX: Record<ActivityRelatedTo['type'], string> = { lead: 'Lead', contact: 'Contact', company: 'Company', opportunity: 'Opportunity' };

/** docs section 14 "Activities" — the standalone feed across every record
 * type, scoped by the caller's own own/team/department/all visibility
 * (`GET /activities/feed`). Rich cards for call/email/meeting/note (the
 * kinds with real body text/outcome), compact one-line rows for status
 * changes, assignments and task completions — matching design/orion-crm-
 * design/rendered-html/Activities.html's two densities.
 *
 * Row sentences are composed from kind + actor + relatedTo, not a bespoke
 * narrative-generation layer, so exact wording won't match the design's
 * illustrative copy ("completed a call with…") — the underlying fields are
 * all real. No CSV export for the global feed this pass (per-record/deal
 * export exists elsewhere; a whole-feed export wasn't built here). */
export function ActivitiesPage() {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<string | undefined>(undefined);
  const [actorId, setActorId] = useState<string | undefined>(undefined);
  const [weekOffset, setWeekOffset] = useState(0);
  const [showLogModal, setShowLogModal] = useState(false);

  const { data: members } = useAssignableMembersQuery();
  const memberLabels = useMemo(() => disambiguateNames(members ?? []), [members]);
  const from = useMemo(() => startOfWeek(weekOffset), [weekOffset]);
  const to = useMemo(() => new Date(from.getTime() + 7 * 86_400_000 - 1), [from]);

  const { data, isLoading, isError } = useActivityFeedQuery({ page: 1, pageSize: 50, kind, actorId, occurredFrom: from.toISOString(), occurredTo: to.toISOString() });
  const { data: summary } = useActivityWeeklySummaryQuery(from.toISOString(), to.toISOString());
  const { data: staleLeads } = useStaleLeadsQuery();

  const activities = data?.data ?? [];

  const groups = useMemo(() => {
    const buckets: { key: string; label: string; sub: string; items: ActivityResponse[] }[] = [];
    for (const a of activities) {
      const { label, sub } = dayLabel(a.occurredAt);
      const last = buckets.at(-1);
      if (last?.sub === sub) last.items.push(a);
      else buckets.push({ key: a.occurredAt, label, sub, items: [a] });
    }
    return buckets;
  }, [activities]);

  const closeLogModal = () => {
    setShowLogModal(false);
    void queryClient.invalidateQueries({ queryKey: ['activity-feed'] });
  };

  const maxRepTotal = Math.max(1, ...(summary?.byRep.map((r) => r.total) ?? [0]));

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Activities' }]} />
      <main className="flex flex-1 flex-col gap-4 overflow-hidden px-7 pb-6 pt-5">
        {showLogModal && <LogActivityModal onClose={closeLogModal} />}

        <div className="flex shrink-0 items-end justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em] text-ink">Activities</h1>
            <span className="text-[13px] text-text-muted">
              Everything your team did across leads, contacts, companies and opportunities{summary ? ` · ${summary.totalCount} this week` : ''}
            </span>
          </div>
          <Button size="sm" onClick={() => setShowLogModal(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Log activity
          </Button>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3">
          <div role="tablist" aria-label="Activity type" className="flex flex-wrap items-center gap-1.5">
            {KIND_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                role="tab"
                aria-selected={kind === f.value}
                onClick={() => setKind(f.value)}
                className={`flex h-8 items-center gap-1.5 rounded-pill px-3 text-[13px] font-semibold ${kind === f.value ? 'bg-night text-white' : 'border border-border bg-surface text-text-secondary hover:bg-surface-subtle'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Menu
              trigger={
                <button type="button" className="flex h-8 items-center gap-1.5 rounded-control border border-border-strong bg-surface px-2.5 text-[13px] font-semibold text-text-secondary">
                  <span className="font-medium text-text-muted">Team:</span> {actorId ? (memberLabels.get(actorId) ?? 'Everyone') : 'Everyone'}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>
              }
              items={[{ label: 'Everyone', onSelect: () => setActorId(undefined) }, ...(members ?? []).map((m) => ({ label: memberLabels.get(m.id) ?? m.displayName, onSelect: () => setActorId(m.id) }))]}
              align="right"
            />
            <div className="flex h-8 items-center gap-1 rounded-control border border-border-strong bg-surface px-1.5">
              <button type="button" aria-label="Previous week" onClick={() => setWeekOffset((w) => w - 1)} className="flex size-6 items-center justify-center rounded-chip hover:bg-surface-subtle">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m15 6-6 6 6 6" />
                </svg>
              </button>
              <span className="px-1 text-[13px] font-semibold text-text-secondary tabular-nums">
                {from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – {to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
              <button
                type="button"
                aria-label="Next week"
                disabled={weekOffset >= 0}
                onClick={() => setWeekOffset((w) => Math.min(0, w + 1))}
                className="flex size-6 items-center justify-center rounded-chip hover:bg-surface-subtle disabled:opacity-30"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5E6470" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m9 6 6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[1fr_320px] gap-5 overflow-hidden">
          <section aria-label="Activity feed" className="flex min-h-0 flex-col overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {isError && (
              <div role="alert" className="m-4 rounded-control bg-danger-soft px-3 py-2 text-sm text-danger">
                Something went wrong loading activities. Please try again.
              </div>
            )}
            {!isLoading && activities.length === 0 && (
              <div className="p-6">
                <EmptyState title="No activity this week" description="Actions across leads, contacts, companies and opportunities will show up here." />
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {groups.map((group) => (
                <div key={group.key}>
                  <div className="flex h-9 items-center justify-between border-b border-divider bg-surface-subtle px-5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
                      {group.label} · {group.sub}
                    </span>
                    <span className="text-xs tabular-nums text-text-muted">{group.items.length} activities</span>
                  </div>
                  {group.items.map((a) => (
                    <ActivityRow key={a.id} activity={a} />
                  ))}
                </div>
              ))}
            </div>
          </section>

          <aside className="flex min-h-0 flex-col gap-4 overflow-y-auto">
            <Card className="flex flex-col gap-4 p-4.5">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-semibold text-ink">Activity this week</h2>
                <span className="text-xs text-text-muted">
                  {from.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–{to.toLocaleDateString('en-US', { day: 'numeric' })}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {SUMMARY_KINDS.map((k) => (
                  <div key={k.kind} className="flex flex-col gap-0.5 rounded-input border border-divider p-2.5">
                    <span className="flex items-center gap-1.5 text-xs text-text-muted">
                      <span className="size-2 rounded-[3px]" style={{ background: k.color }} />
                      {k.label}
                    </span>
                    <span className="text-[22px] font-[650] leading-none tabular-nums tracking-[-0.02em]">{summary?.byKind.find((b) => b.kind === k.kind)?.count ?? 0}</span>
                  </div>
                ))}
              </div>

              {summary && summary.byRep.length > 0 && (
                <div className="flex flex-col gap-3">
                  <span className="text-xs font-semibold uppercase tracking-[0.06em] text-text-muted">By rep</span>
                  {summary.byRep.slice(0, 4).map((rep) => (
                    <div key={rep.membershipId} className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2 text-[13px]">
                        <Avatar name={rep.displayName} size="xs" />
                        <span className="flex-1 font-semibold">{rep.displayName}</span>
                        <span className="tabular-nums text-text-secondary">{rep.total}</span>
                      </div>
                      <div className="flex h-1.5 gap-px overflow-hidden rounded-pill bg-divider">
                        {SUMMARY_KINDS.map((k) => {
                          const count = rep.byKind.find((b) => b.kind === k.kind)?.count ?? 0;
                          if (count === 0) return null;
                          return <span key={k.kind} style={{ width: `${(count / maxRepTotal) * 100}%`, background: k.color }} />;
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Link to="/reports" className="flex items-center gap-1 text-[13px] font-semibold text-primary-soft-text hover:underline">
                Open activity report
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2439B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
            </Card>

            {staleLeads && staleLeads.count > 0 && (
              <Card className="flex items-start gap-3 p-4">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-input" style={{ background: 'var(--color-amber-soft)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-amber-text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 7v5l3 2" />
                  </svg>
                </span>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold text-ink">
                    {staleLeads.count} lead{staleLeads.count === 1 ? '' : 's'} untouched for 7+ days
                  </span>
                  <span className="text-[13px] leading-4.5 text-text-muted">{staleLeads.sample.map((l) => l.name).join(', ')} {staleLeads.count > staleLeads.sample.length ? 'and others ' : ''}have no logged activity this week.</span>
                  <Link to="/leads" className="mt-0.5 text-[13px] font-semibold text-primary-soft-text hover:underline">
                    Review leads
                  </Link>
                </div>
              </Card>
            )}
          </aside>
        </div>
      </main>
    </>
  );
}

function ActivityRow({ activity: a }: { activity: ActivityResponse }) {
  const style = KIND_STYLE[a.kind] ?? { bg: '#EFEEEA', fg: '#5E6470', path: 'M12 8v4l3 2' };
  const time = new Date(a.occurredAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  if (RICH_KINDS.has(a.kind)) {
    return (
      <article className="flex gap-3.5 border-b border-divider px-5 py-3.5 last:border-0">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full" style={{ background: style.bg }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={style.fg} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {style.isRect && <rect x="3" y="5" width="18" height="14" rx="2" />}
            <path d={style.path} />
          </svg>
        </span>
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <Avatar name={a.actor?.displayName ?? 'System'} size="xs" />
            <span className="flex-1 truncate text-sm leading-5 text-text-secondary">
              <strong className="font-semibold text-ink">{a.actor?.displayName ?? 'System'}</strong> logged a {KIND_LABELS[a.kind]?.toLowerCase()}
              {a.relatedTo && (
                <>
                  {' with '}
                  <RelatedLink relatedTo={a.relatedTo}>
                    <span className="font-semibold text-ink hover:underline">{a.relatedTo.label}</span>
                  </RelatedLink>
                </>
              )}
            </span>
            <span className="shrink-0 text-xs tabular-nums text-text-muted">{time}</span>
          </div>
          {(a.relatedTo || a.outcome) && (
            <div className="flex flex-wrap items-center gap-2">
              {a.relatedTo && (
                <span className="flex h-5.5 items-center gap-1.5 rounded-[6px] border border-border px-2 text-xs font-medium text-text-secondary">
                  {RELATED_PREFIX[a.relatedTo.type]} · {a.relatedTo.label}
                </span>
              )}
              {a.outcome && (
                <span className="text-xs text-text-muted">
                  Outcome: {a.outcome}
                  {a.metadata.durationSeconds ? ` · ${Math.round(Number(a.metadata.durationSeconds) / 60)} min` : ''}
                </span>
              )}
            </div>
          )}
          {a.bodyText && <div className="rounded-control border border-divider bg-surface-subtle px-3 py-2.5 text-[13px] leading-4.75 text-text-secondary">{a.bodyText}</div>}
        </div>
      </article>
    );
  }

  return (
    <div className="flex items-center gap-3.5 border-b border-divider py-2.5 pl-6 pr-5 last:border-0">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full" style={{ background: style.bg }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={style.fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d={style.path} />
        </svg>
      </span>
      <span className="flex-1 truncate text-[13px] text-text-secondary">
        <strong className="font-semibold text-ink">{a.actor?.displayName ?? 'System'}</strong> {KIND_LABELS[a.kind]?.toLowerCase()}
        {a.relatedTo && (
          <>
            {' '}
            <RelatedLink relatedTo={a.relatedTo}>
              <span className="font-semibold text-ink hover:underline">{a.relatedTo.label}</span>
            </RelatedLink>
          </>
        )}
        {a.subject && <span className="text-text-muted"> · {a.subject}</span>}
      </span>
      <span className="shrink-0 text-xs tabular-nums text-text-muted">{time}</span>
    </div>
  );
}

type RecordType = 'lead' | 'contact' | 'company' | 'opportunity';
const RECORD_TABS: Array<{ key: RecordType; label: string }> = [
  { key: 'lead', label: 'Lead' },
  { key: 'contact', label: 'Contact' },
  { key: 'company', label: 'Company' },
  { key: 'opportunity', label: 'Opportunity' },
];

/** The design's "Log activity" button opens the same composer every detail
 * page already uses, but the standalone feed has no record of its own —
 * `logActivity` requires exactly one relation, so this picks one first. */
function LogActivityModal({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<RecordType>('lead');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<{ id: string; label: string } | null>(null);

  const { data: leads } = useLeadsQuery({ q, pageSize: 6 });
  const { data: contacts } = useContactsQuery({ q, pageSize: 6 });
  const { data: companies } = useCompaniesQuery({ q, pageSize: 6 });
  const { data: deals } = useDealsQuery({ q, pageSize: 6 });

  const options =
    type === 'lead'
      ? (leads?.data ?? []).map((l) => ({ id: l.id, label: l.fullName }))
      : type === 'contact'
        ? (contacts?.data ?? []).map((c) => ({ id: c.id, label: c.fullName }))
        : type === 'company'
          ? (companies?.data ?? []).map((c) => ({ id: c.id, label: c.name }))
          : (deals?.data ?? []).map((d) => ({ id: d.id, label: d.name }));

  const relationProp = { leadId: type === 'lead' ? selected?.id : undefined, contactId: type === 'contact' ? selected?.id : undefined, companyId: type === 'company' ? selected?.id : undefined, opportunityId: type === 'opportunity' ? selected?.id : undefined };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-night/42" onClick={onClose} />
      <Card className="fixed left-1/2 top-1/2 z-50 flex max-h-[80vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col gap-3 overflow-hidden p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-ink">Log activity</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="flex size-7 items-center justify-center rounded-chip hover:bg-surface-subtle">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#454A54" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        {!selected ? (
          <>
            <div className="flex gap-1.5">
              {RECORD_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  className={`flex h-8 items-center rounded-chip px-3 text-[13px] font-semibold ${type === t.key ? 'bg-primary-soft text-primary-soft-text' : 'border border-border text-text-secondary'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${RECORD_TABS.find((t) => t.key === type)!.label.toLowerCase()}s…`} className="h-9 rounded-control border border-border-strong px-3 text-sm" />
            <div className="flex flex-col gap-1 overflow-y-auto">
              {options.map((o) => (
                <button key={o.id} type="button" onClick={() => setSelected(o)} className="rounded-control px-2.5 py-2 text-left text-sm hover:bg-surface-subtle">
                  {o.label}
                </button>
              ))}
              {q && options.length === 0 && <p className="px-2.5 py-2 text-sm text-text-muted">No matches.</p>}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-text-muted">Logging against</span>
              <span className="font-semibold text-ink">{selected.label}</span>
              <button type="button" onClick={() => setSelected(null)} className="text-xs font-semibold text-primary-soft-text hover:underline">
                Change
              </button>
            </div>
            <LogActivityComposer {...relationProp} />
          </>
        )}
      </Card>
    </>
  );
}
